import { ORDER_STATUSES, type OrderStatus } from '@blackarrow/types';
import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';

import { isoDate } from '@/lib/date-presets';
import { isPortalHost, isSiteHost, toPortal, toSite } from '@/lib/domains';
import { can } from '@/lib/permissions';
import { queryClient } from '@/lib/query-client';
import { getSession, homeFor, loginPathFor, type Surface } from '@/lib/session';

import { MERCHANT_TODAY_TILES } from './merchant/merchantStatus';
import { validateDashboardSearch } from './ops/dashboard-search';
import { validateHubSearch } from './ops/hub-search';
import { validateInsightsSearch } from './ops/insights-search';
import { validateOrdersSearch } from './ops/orders-search';

import { rootRoute } from './root';

/**
 * Three route groups, three bundles.
 *
 * ops and merchant are one deployed app (ARCHITECTURE.md §8) but they are not
 * one download: a merchant should never pull the ops console's code, and the
 * public tracking page must stay small because it opens on a cheap Android
 * phone over a patchy connection. lazyRouteComponent is what makes each group
 * its own chunk.
 */

/**
 * The guard on a console route group.
 *
 * This is convenience, not access control — it decides what to *render*, and
 * the token in localStorage is the only thing it can read. Every actual
 * decision is the server's: the token carries one ability, the route groups
 * check for exactly that one, and merchant rows are bound by a global scope.
 * Someone who edits localStorage gets a console that 401s on every call.
 *
 * The surface check matters as much as the token check. An ops user who
 * navigates to /merchant has a perfectly valid token that the merchant
 * endpoints will refuse, and a console full of failed requests is a worse
 * answer than the login screen.
 *
 * Checked before any of that: which domain this even is. The portal routes
 * are only meant to be reached at app.blackarrow.com.pk — a stray deep link
 * to the marketing domain bounces to the same path on the portal host and
 * runs this same guard again there, rather than rendering (or 401ing) on the
 * wrong domain. That second run always sees isSiteHost() as false, so it
 * falls straight through to the surface check below — one domain hop, never
 * two in a row, so this cannot loop with the portal-host branch.
 */
function requireSurface(surface: Surface, location: { pathname: string; href: string }): void {
    if (isSiteHost()) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ href: toPortal(location.href), statusCode: 307 });
    }

    const session = getSession();

    if (session?.surface === surface) {
        return;
    }

    // Throwing a redirect is TanStack Router's control-flow API, not an error
    // path — the router catches it and navigates.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
        to: loginPathFor(),
        // So a deep link survives the round trip through the login screen.
        search: { next: location.pathname },
        replace: true,
    });
}

/**
 * The guard on a merchant screen that only some merchant-side logins may open.
 *
 * Same standing as requireSurface: convenience, not access control. A
 * merchant_orders_manager who types /merchant/statement would otherwise land on
 * a screen whose every request 403s; this sends them home instead. The server
 * refuses the requests either way.
 *
 * `me` is almost always already cached (the layout asked for it), so this is
 * usually free. If it cannot be fetched the guard steps aside rather than
 * inventing a verdict — the screen's own error state is the honest answer to a
 * network failure, and a 401 is the API layer's business.
 *
 * Imported lazily so the merchant API module stays out of the entry bundle.
 */
async function requireMerchantPermission(permission: string): Promise<void> {
    const { merchantMeQueryOptions } = await import('@/api/merchant');

    await requirePermission(merchantMeQueryOptions(), permission, '/merchant');
}

/**
 * The ops console's twin: a screen the nav hides is a screen a typed URL
 * cannot open either. Same courtesy-not-security footing as the nav — the
 * endpoints behind the screen do the real refusing.
 */
async function requireOpsPermission(permission: string): Promise<void> {
    const { opsMeQueryOptions } = await import('@/api/ops');

    await requirePermission(opsMeQueryOptions(), permission, '/ops');
}

async function requirePermission(
    meQuery: {
        queryKey: readonly unknown[];
        queryFn: () => Promise<{ data: { permissions: readonly string[] } }>;
        staleTime: number;
    },
    permission: string,
    fallback: '/merchant' | '/ops',
): Promise<void> {
    let permitted = true;

    try {
        // One attempt, not the client's default retries: a guard that waits out
        // several seconds of backoff before drawing anything is worse than one
        // that steps aside and lets the screen show its own error.
        const me = await queryClient.ensureQueryData({ ...meQuery, retry: false });
        permitted = can(me.data, permission);
    } catch {
        return;
    }

    if (!permitted) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: fallback, replace: true });
    }
}

/**
 * Someone already signed in usually has no use for the login screen — except
 * when `next` is the one thing that sent them here: requireSurface bouncing a
 * token that does not cover the console it was aimed at. There is one login
 * screen now, so that case cannot be told apart by which door it is; it shows
 * up as a `next` outside the signed-in account's own console. Render the form
 * then, so there is a way to sign in as the account that belongs there —
 * otherwise a merchant session wandering onto /ops and an ops session
 * wandering onto /merchant would each bounce straight back to their own
 * console and neither could ever reach the other's door to switch.
 *
 * Domain-checked first, same reasoning as requireSurface: a login link on the
 * marketing domain (e.g. an emailed `blackarrow.com.pk/login?next=…`) bounces
 * to the same path — `next` included, since `href` carries the full query
 * string — on the portal host, where this guard runs again and only then
 * evaluates the session.
 */
function redirectIfSignedIn(location: { href: string }, next: string | undefined): void {
    if (isSiteHost()) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ href: toPortal(location.href), statusCode: 307 });
    }

    const session = getSession();

    if (session === null) {
        return;
    }

    const home = homeFor(session.surface);

    if (next === undefined || next.startsWith(home)) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: home, replace: true });
    }
}

/**
 * The guard on the marketing-only public routes (register, tracking).
 *
 * These never render on the portal host — a stray hit there (an old bookmark,
 * a mistyped link) bounces to the same path on the marketing domain.
 */
function siteOnly({ location }: { location: { href: string } }): void {
    if (isPortalHost()) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ href: toSite(location.href), statusCode: 307 });
    }
}

/** `?next=` is only ever written by our own guards, so it is a plain string. */
function validateNext(search: Record<string, unknown>): { next?: string } {
    const { next } = search;

    return typeof next === 'string' && next.startsWith('/') ? { next } : {};
}

const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    beforeLoad: () => {
        const session = getSession();

        // The portal host never shows the marketing homepage — a bare "/"
        // there means either "not signed in" (staff sign-in) or "signed in"
        // (straight to the console).
        if (isPortalHost()) {
            // eslint-disable-next-line @typescript-eslint/only-throw-error
            throw redirect({
                to: session === null ? '/login' : homeFor(session.surface),
                replace: true,
            });
        }

        if (session === null) {
            // Everyone else sees the homepage below.
            return;
        }

        // A signed-in user has no use for the marketing page — send them
        // straight to their own console. Which console depends on who they
        // are, which is the whole reason the surface is stored alongside the
        // token. On a configured production build that console lives on the
        // portal domain; unconfigured (dev, preview), it's the same origin,
        // exactly as before the domain split existed.
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw isSiteHost()
            ? redirect({ href: toPortal(homeFor(session.surface)), statusCode: 307 })
            : redirect({ to: homeFor(session.surface), replace: true });
    },
    component: lazyRouteComponent(() => import('./home/HomePage'), 'HomePage'),
});

const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    validateSearch: validateNext,
    beforeLoad: ({ location, search }) => {
        redirectIfSignedIn(location, search.next);
    },
    component: lazyRouteComponent(() => import('./LoginPage'), 'LoginPage'),
});

/**
 * The old merchant door, kept as a redirect so a bookmark or an emailed link
 * still lands somewhere useful — everyone signs in at /login now.
 *
 * Sits outside the /merchant group deliberately: a login screen behind the
 * guard that sends you to it is a redirect loop.
 */
const merchantLoginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/merchant/login',
    validateSearch: validateNext,
    beforeLoad: ({ location, search }) => {
        // Same domain-hop reasoning as requireSurface/redirectIfSignedIn: land
        // on the portal host before deciding anything else.
        if (isSiteHost()) {
            // eslint-disable-next-line @typescript-eslint/only-throw-error
            throw redirect({ href: toPortal(location.href), statusCode: 307 });
        }

        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: '/login', search, replace: true });
    },
});

const opsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/ops',
    beforeLoad: ({ location }) => {
        requireSurface('ops', location);
    },
    component: lazyRouteComponent(() => import('./ops/OpsLayout'), 'OpsLayout'),
});

const opsIndexRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/',
    validateSearch: validateDashboardSearch,
    component: lazyRouteComponent(() => import('./ops/OpsDashboard'), 'OpsDashboard'),
});

/**
 * Every ops orders filter lives in the URL — see ./ops/orders-search for the
 * schema and why it is there rather than in component state.
 */
/**
 * The dashboard's historical twin — see OpsInsights for why it is a separate
 * screen and not more fields on the dashboard. Same URL-as-state contract as
 * the dashboard's filters, kept in its own search schema (./ops/insights-
 * search) since the two routes are free to diverge.
 */
const opsInsightsRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/insights',
    beforeLoad: () => requireOpsPermission('reports.view'),
    validateSearch: validateInsightsSearch,
    component: lazyRouteComponent(() => import('./ops/OpsInsights'), 'OpsInsights'),
});

const opsOrdersRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/orders',
    validateSearch: validateOrdersSearch,
    component: lazyRouteComponent(() => import('./ops/OrdersTable'), 'OrdersTable'),
});

/**
 * Declared before the $orderId route so "/ops/orders/new" is not read as an
 * order whose id is "new".
 *
 * `merchant` is in the URL because it is step 1 of two and losing it to a
 * refresh means retyping it — see the screen for why the merchant has to be
 * picked before the form can render at all.
 */
const opsNewOrderRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/orders/new',
    beforeLoad: () => requireOpsPermission('orders.create'),
    validateSearch: (search: Record<string, unknown>): { merchant?: number } => {
        const id = Number(search.merchant);

        return Number.isInteger(id) && id > 0 ? { merchant: id } : {};
    },
    component: lazyRouteComponent(() => import('./ops/NewOrderPage'), 'NewOrderPage'),
});

const opsOrderDetailRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/orders/$orderId',
    component: lazyRouteComponent(() => import('./ops/OrderDetail'), 'OrderDetail'),
});

/**
 * The hub station. Its own route rather than a panel on another screen: it is
 * a screen somebody stands at with a scanner for an hour.
 *
 * New, declined and rescheduled parcels are all booked back in here; `mode`
 * picks which door a scan goes through.
 */
const opsHubScanRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/hub',
    beforeLoad: () => requireOpsPermission('hub.scan_inbound'),
    validateSearch: validateHubSearch,
    component: lazyRouteComponent(() => import('./ops/HubScanPage'), 'HubScanPage'),
});

/**
 * The old rescheduled check-in screen, kept as a redirect so a bookmark still
 * lands on the right tab of the combined hub screen.
 */
const opsRescheduleCheckInRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/hub/reschedule',
    beforeLoad: () => {
        // Throwing a redirect is TanStack Router's control-flow API, not an error.
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: '/ops/hub', search: { mode: 'rescheduled' }, replace: true });
    },
});

const opsRidersRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/riders',
    beforeLoad: () => requireOpsPermission('riders.manage'),
    component: lazyRouteComponent(() => import('./ops/RidersPage'), 'RidersPage'),
});

/**
 * Where a rider has been, reached from their row on the riders list.
 *
 * A route of its own rather than a tab on RidersPage, because a rider-day is
 * the unit and the date belongs in the URL: "where was Ali last Wednesday" is
 * a link somebody pastes into a message, and a modal cannot be one.
 */
const opsRiderRouteRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/riders/$riderId/route',
    beforeLoad: () => requireOpsPermission('fleet.view_rider_location'),
    component: lazyRouteComponent(() => import('./ops/RiderRoutePage'), 'RiderRoutePage'),
});

const opsHubManagersRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/hub-managers',
    beforeLoad: () => requireOpsPermission('staff.manage'),
    component: lazyRouteComponent(() => import('./ops/HubManagersPage'), 'HubManagersPage'),
});

const opsMerchantsRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/merchants',
    beforeLoad: () => requireOpsPermission('merchants.manage'),
    component: lazyRouteComponent(() => import('./ops/MerchantsPage'), 'MerchantsPage'),
});

const opsCashDeskRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/cash',
    beforeLoad: () => requireOpsPermission('cash.reconcile_rider'),
    component: lazyRouteComponent(() => import('./ops/CashDeskPage'), 'CashDeskPage'),
});

const opsSettlementsRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/settlements',
    beforeLoad: () => requireOpsPermission('settlements.create'),
    component: lazyRouteComponent(() => import('./ops/SettlementsPage'), 'SettlementsPage'),
});

const opsRiderExpensesRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/fleet/expenses',
    beforeLoad: () => requireOpsPermission('fleet.record_rider_expenses'),
    component: lazyRouteComponent(() => import('./ops/RiderExpensesPage'), 'RiderExpensesPage'),
});

/** Company expenses, monthly dues and partner investments (ADR 0026). */
const opsExpensesRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/finance/expenses',
    beforeLoad: () => requireOpsPermission('finance.record_hub_expenses'),
    component: lazyRouteComponent(() => import('./ops/ExpensesPage'), 'ExpensesPage'),
});

const opsCashProfitRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/finance/cash-profit',
    beforeLoad: () => requireOpsPermission('finance.view_pnl'),
    component: lazyRouteComponent(() => import('./ops/cash-profit/CashProfitPage'), 'CashProfitPage'),
});

const opsExceptionsRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/exceptions',
    component: lazyRouteComponent(
        () => import('./ops/DeliveryExceptionsPage'),
        'DeliveryExceptionsPage',
    ),
});

const opsMessagesRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/messages',
    beforeLoad: () => requireOpsPermission('notifications.send'),
    component: lazyRouteComponent(() => import('./ops/MessageQueuePage'), 'MessageQueuePage'),
});

const opsTicketsRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/tickets',
    beforeLoad: () => requireOpsPermission('tickets.view_all'),
    component: lazyRouteComponent(() => import('./ops/OpsTicketsPage'), 'OpsTicketsPage'),
});

const opsNotificationsRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/notifications',
    component: lazyRouteComponent(() => import('./ops/OpsNotifications'), 'OpsNotifications'),
});

const opsSettingsRoute = createRoute({
    getParentRoute: () => opsRoute,
    path: '/settings',
    component: lazyRouteComponent(() => import('./ops/SettingsPage'), 'SettingsPage'),
});

const merchantRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/merchant',
    beforeLoad: ({ location }) => {
        requireSurface('merchant', location);
    },
    component: lazyRouteComponent(() => import('./merchant/MerchantLayout'), 'MerchantLayout'),
});

/**
 * No `validateSearch`, unlike the ops dashboard: merchant home is a live board
 * of where things are *right now*, not a report over a window — see
 * MerchantDashboard for why a date window never fitted any tile on it. An old
 * `?from=&to=` link still resolves; the params are simply ignored.
 */
const merchantIndexRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/',
    component: lazyRouteComponent(() => import('./merchant/MerchantDashboard'), 'MerchantDashboard'),
});

/**
 * The one merchant screen with a date window in the URL, so a filtered period
 * survives a refresh and can be pasted into a message.
 *
 * The dashboard next door deliberately has none (it is a live board of right
 * now). Here the window is the whole point — and absent dates mean the
 * server's default of the last 30 days rather than "all time", because a daily
 * series has to be bounded to be drawable at all.
 */
const merchantInsightsRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/insights',
    beforeLoad: () => requireMerchantPermission('reports.view_own'),
    validateSearch: (search: Record<string, unknown>): { from?: string; to?: string } => {
        const from = isoDate(search.from);
        const to = isoDate(search.to);

        return {
            ...(from !== undefined && { from }),
            ...(to !== undefined && { to }),
        };
    },
    component: lazyRouteComponent(() => import('./merchant/MerchantInsights'), 'MerchantInsights'),
});

const merchantOrdersRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/orders',
    /**
     * Same contract as the ops table: the dashboard's stage cards link in with
     * `status`, and the topbar search lands here with `q` — the same parameter
     * name the ops console uses, because the two tables answer the same
     * question and a link that works on one should read on the other.
     *
     * `status` takes one status or several. The single form is what a stage
     * card sends; the list form is what "Account Balance" sends, because the
     * orders behind that number are ten statuses, not one. Both normalise to
     * an array so the table has one shape to read.
     *
     * `created_from`/`created_to` are the booking window, bare `YYYY-MM-DD`,
     * named for the API parameters they become rather than the ops table's
     * shorter `from`/`to` — this table has only ever filtered on booking date,
     * and a bare `from` next to a delivered-day screen invites the wrong
     * reading. Insights' cohort tiles link in with them so the parcels the
     * tile counted are the parcels the table lists.
     *
     * `happened` + `happened_from`/`happened_to` are the dashboard's "today"
     * tiles: the orders that were delivered / declined / rescheduled on those
     * days, whatever they are now. All three or none.
     */
    validateSearch: (
        search: Record<string, unknown>,
    ): {
        status?: OrderStatus[];
        q?: string;
        created_from?: string;
        created_to?: string;
        // Checked against MERCHANT_TODAY_TILES below, but typed wide: every
        // route's search reaches each navigate reducer merged, and the ops
        // table's `happened` takes more tiles than this one does.
        happened?: string;
        happened_from?: string;
        happened_to?: string;
    } => {
        const { status, q, created_from: createdFrom, created_to: createdTo } = search;

        const candidates = typeof status === 'string' ? [status] : Array.isArray(status) ? status : [];

        const validStatuses = candidates.filter(
            (value): value is OrderStatus =>
                typeof value === 'string' && ORDER_STATUSES.includes(value as OrderStatus),
        );

        const term = typeof q === 'string' && q.trim() !== '' ? q.trim() : undefined;

        const from = isoDate(createdFrom);
        const to = isoDate(createdTo);

        const happened = MERCHANT_TODAY_TILES.find((tile) => tile === search.happened);
        const happenedFrom = isoDate(search.happened_from);
        const happenedTo = isoDate(search.happened_to);

        return {
            ...(validStatuses.length > 0 && { status: validStatuses }),
            ...(term !== undefined && { q: term }),
            ...(from !== undefined && { created_from: from }),
            ...(to !== undefined && { created_to: to }),
            ...(happened !== undefined &&
                happenedFrom !== undefined &&
                happenedTo !== undefined && {
                    happened,
                    happened_from: happenedFrom,
                    happened_to: happenedTo,
                }),
        };
    },
    component: lazyRouteComponent(() => import('./merchant/MerchantOrders'), 'MerchantOrders'),
});

/**
 * Declared before the $orderId route so "/merchant/orders/new" is not read as
 * an order whose id is "new".
 */
const merchantNewOrderRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/orders/new',
    /**
     * Which intake mode to open on, so a link can land on the bulk grid.
     * Read once as an initial value and not written back
     * when the tab changes — see the note on NewOrderPage for why the tab is
     * not itself a route.
     */
    validateSearch: (search: Record<string, unknown>): { tab?: 'bulk' } =>
        search.tab === 'bulk' ? { tab: 'bulk' } : {},
    component: lazyRouteComponent(() => import('./merchant/NewOrderPage'), 'NewOrderPage'),
});

/**
 * The register. Declared before the settlements routes because it is the money
 * screen a merchant opens first — Payouts answers what we sent, this answers
 * where the money has been, and most of a balance has not been sent yet.
 */
const merchantStatementRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/statement',
    beforeLoad: () => requireMerchantPermission('settlements.view_own'),
    component: lazyRouteComponent(
        () => import('./merchant/MerchantStatement'),
        'MerchantStatement',
    ),
});

const merchantSettlementsRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/settlements',
    beforeLoad: () => requireMerchantPermission('settlements.view_own'),
    component: lazyRouteComponent(
        () => import('./merchant/MerchantSettlements'),
        'MerchantSettlements',
    ),
});

const merchantSettlementDetailRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/settlements/$settlementId',
    beforeLoad: () => requireMerchantPermission('settlements.view_own'),
    component: lazyRouteComponent(
        () => import('./merchant/MerchantSettlementDetail'),
        'MerchantSettlementDetail',
    ),
});

const merchantOrderDetailRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/orders/$orderId',
    component: lazyRouteComponent(
        () => import('./merchant/MerchantOrderDetail'),
        'MerchantOrderDetail',
    ),
});

/**
 * Fixing a booking. A route rather than a modal over the detail screen: it is
 * the whole intake form, it holds a draft that has to survive leaving the page,
 * and a merchant who has typed half a corrected address wants a URL they can
 * come back to.
 */
const merchantEditOrderRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/orders/$orderId/edit',
    component: lazyRouteComponent(() => import('./merchant/EditOrderPage'), 'EditOrderPage'),
});

/**
 * Was a dialog opened from the profile popover; now a route, matching ops.
 */
const merchantSettingsRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/settings',
    component: lazyRouteComponent(
        () => import('./merchant/MerchantSettingsPage'),
        'MerchantSettingsPage',
    ),
});

/**
 * The Shopify admin page sends a merchant here with a one-time code to link
 * their shop (ADR 0050 §1). Same permission as Settings ▸ Integrations; a
 * signed-out merchant goes through login and lands back here with the search
 * intact.
 */
const merchantShopifyLinkRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/integrations/shopify/link',
    validateSearch: (search: Record<string, unknown>): { shop: string; code: string } => ({
        shop: typeof search.shop === 'string' ? search.shop : '',
        code: typeof search.code === 'string' ? search.code : '',
    }),
    beforeLoad: () => requireMerchantPermission('api_keys.manage'),
    component: lazyRouteComponent(() => import('./merchant/ShopifyLinkPage'), 'ShopifyLinkPage'),
});

/**
 * Refused parcels coming back from a rider, scanned in (ADR 0040). No
 * permission gate, like the notification it is reached from: any user of the
 * merchant may take a parcel from the rider.
 */
const merchantReceiveReturnsRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/returns',
    component: lazyRouteComponent(
        () => import('./merchant/ReceiveReturnsPage'),
        'ReceiveReturnsPage',
    ),
});

/**
 * The design's isNotifications screen, now reading a real feed —
 * `user_notifications` on the server, written by whatever finished a job for
 * this user. Ops has the same screen at /ops/notifications.
 */
const merchantNotificationsRoute = createRoute({
    getParentRoute: () => merchantRoute,
    path: '/notifications',
    component: lazyRouteComponent(
        () => import('./merchant/MerchantNotifications'),
        'MerchantNotifications',
    ),
});

/**
 * Public. No auth, no app shell, no Black Arrow chrome competing with the
 * merchant's brand.
 */
const trackRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/track/$trackingNo',
    beforeLoad: siteOnly,
    component: lazyRouteComponent(() => import('./track/TrackingPage'), 'TrackingPage'),
});

/**
 * No tracking number in the URL — e.g. reached directly from a nav link
 * rather than an SMS. Same page/bundle as `trackRoute`; `TrackingPage`
 * renders its search-entry state when `trackingNo` is undefined.
 */
const trackIndexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/track',
    beforeLoad: siteOnly,
    component: lazyRouteComponent(() => import('./track/TrackingPage'), 'TrackingPage'),
});

/**
 * Reached from the address-confirmation SMS. `t` is the signed token, passed
 * straight through to the API — the SPA never inspects it, and the server is
 * the only thing that can say whether it is valid.
 */
/** Public. Ends in "Application received", not a redirect — see RegisterPage. */
const registerRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/register',
    beforeLoad: siteOnly,
    component: lazyRouteComponent(() => import('./register/RegisterPage'), 'RegisterPage'),
});

/**
 * Reached from the merchant's verification email. `t` is the signed token,
 * passed straight through to the API exactly as the address-confirmation link
 * below does — the SPA never inspects it.
 *
 * siteOnly for the same reason /register is: the merchant has no session yet,
 * so the portal host has nothing to show them.
 */
const verifyEmailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/verify-email',
    validateSearch: (search: Record<string, unknown>): { t?: string } => {
        const { t } = search;

        return typeof t === 'string' && t !== '' ? { t } : {};
    },
    beforeLoad: siteOnly,
    component: lazyRouteComponent(
        () => import('./verify-email/VerifyEmailPage'),
        'VerifyEmailPage',
    ),
});

/**
 * Public legal pages. No `siteOnly`: the login screen on the portal host links
 * here, and these are plain documents that render the same on either domain.
 */
const privacyRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/privacy',
    component: lazyRouteComponent(() => import('./legal/PrivacyPage'), 'PrivacyPage'),
});

/**
 * The Merchant API guide and reference. Public, and like the legal pages not
 * `siteOnly`: Settings ▸ API keys on the portal host links here.
 */
const developersRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/developers',
    component: lazyRouteComponent(() => import('./developers/DevelopersPage'), 'DevelopersPage'),
});

const termsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/terms',
    component: lazyRouteComponent(() => import('./legal/TermsPage'), 'TermsPage'),
});

const confirmAddressRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/track/$trackingNo/confirm',
    validateSearch: (search: Record<string, unknown>): { t?: string } => {
        const { t } = search;

        return typeof t === 'string' && t !== '' ? { t } : {};
    },
    beforeLoad: siteOnly,
    component: lazyRouteComponent(
        () => import('./track/ConfirmAddressPage'),
        'ConfirmAddressPage',
    ),
});

export const routeTree = rootRoute.addChildren([
    indexRoute,
    loginRoute,
    merchantLoginRoute,
    opsRoute.addChildren([
        opsIndexRoute,
        opsInsightsRoute,
        opsOrdersRoute,
        opsNewOrderRoute,
        opsOrderDetailRoute,
        opsHubScanRoute,
        opsRescheduleCheckInRoute,
        opsRidersRoute,
        opsRiderRouteRoute,
        opsMerchantsRoute,
        opsHubManagersRoute,
        opsCashDeskRoute,
        opsSettlementsRoute,
        opsRiderExpensesRoute,
        opsExpensesRoute,
        opsCashProfitRoute,
        opsExceptionsRoute,
        opsMessagesRoute,
        opsTicketsRoute,
        opsNotificationsRoute,
        opsSettingsRoute,
    ]),
    merchantRoute.addChildren([
        merchantIndexRoute,
        merchantInsightsRoute,
        merchantOrdersRoute,
        merchantNewOrderRoute,
        merchantOrderDetailRoute,
        merchantEditOrderRoute,
        merchantStatementRoute,
        merchantSettlementsRoute,
        merchantSettlementDetailRoute,
        merchantSettingsRoute,
        merchantShopifyLinkRoute,
        merchantNotificationsRoute,
        merchantReceiveReturnsRoute,
    ]),
    trackRoute,
    trackIndexRoute,
    confirmAddressRoute,
    registerRoute,
    verifyEmailRoute,
    privacyRoute,
    termsRoute,
    developersRoute,
]);
